import React, { useState, useCallback, useRef, useMemo } from "react"
import API from "../../utils/API"
import DetectionZoneCanvas from "./DetectionZoneCanvas"

const CAPTURE_MODES = [
  { value: "all", label: "Tout" },
  { value: "high_quality", label: "Haute qualité" },
  { value: "custom", label: "Personnalisé" },
]

const PUSH_MODES = [
  { value: "interval", label: "Intervalle" },
  { value: "fast", label: "Rapide" },
  { value: "best", label: "Optimal" },
  { value: "unlimit", label: "Illimité" },
  { value: "over_line", label: "Passage de ligne" },
]

const FACE_PUSH_MODES = PUSH_MODES.filter((m) => m.value !== "interval")

const FACE_TARGETS = [
  { value: "face", label: "Visage" },
  { value: "pedestrian", label: "Piéton" },
]

const STRUCTURE_TARGETS = [
  { value: "face", label: "Visage" },
  { value: "pedestrian", label: "Piéton" },
  { value: "vehicle", label: "Véhicule" },
  { value: "non-motor", label: "Non-motorisé" },
  { value: "plate", label: "Plaque" },
]

const FILTER_FIELDS = [
  { key: "face_filter_roll", label: "Rotation (roll)", min: 0, max: 180, rec: "≤ 45°" },
  { key: "face_filter_yaw", label: "Lacet (yaw)", min: 0, max: 100, rec: "≤ 35°" },
  { key: "face_filter_pitch", label: "Tangage (pitch)", min: 0, max: 100, rec: "≤ 45°" },
  { key: "face_filter_blurness", label: "Flou (blurness)", min: 0, max: 100, rec: "≤ 60" },
  { key: "face_filter_face_min", label: "Taille min visage", min: 20, max: 380, rec: "≥ 80px" },
  { key: "face_filter_body_min", label: "Taille min corps", min: 20, max: 380, rec: "≥ 80px" },
]

const STRUCT_QUALITY_FIELDS = [
  { key: "structure_face_quality", label: "Qualité visage" },
  { key: "structure_pedestrian_quality", label: "Qualité piéton" },
  { key: "structure_vehicle_quality", label: "Qualité véhicule" },
  { key: "structure_non_motor_quality", label: "Qualité non-motorisé" },
]

function areasToCanvasZones(areasParam) {
  if (!areasParam?.enable || !areasParam?.areas?.length) return null
  return areasParam.areas
    .map((area) => {
      if (!area.points?.length) return null
      const xs = area.points.map((p) => (p.x <= 1 ? p.x * 10000 : p.x))
      const ys = area.points.map((p) => (p.y <= 1 ? p.y * 10000 : p.y))
      return {
        enable: true,
        start_x: Math.round(Math.min(...xs)),
        start_y: Math.round(Math.min(...ys)),
        width: Math.round(Math.max(...xs) - Math.min(...xs)),
        height: Math.round(Math.max(...ys) - Math.min(...ys)),
      }
    })
    .filter(Boolean)
}

function canvasZonesToAreas(zones) {
  if (!zones?.length) return undefined
  return {
    enable: true,
    areas: zones.map((z, i) => ({
      areaId: i + 1,
      type: 2,
      points: [
        { x: z.start_x / 10000, y: z.start_y / 10000 },
        { x: (z.start_x + z.width) / 10000, y: z.start_y / 10000 },
        { x: (z.start_x + z.width) / 10000, y: (z.start_y + z.height) / 10000 },
        { x: z.start_x / 10000, y: (z.start_y + z.height) / 10000 },
      ],
    })),
  }
}

function buildDefaultForm() {
  return {
    monitor_name: "",
    device_id: 1,
    channel_type: 1,
    channel_id: 0,
    enable: true,
    alg_type: "",
    intelli_frame_enable: false,
    full_image_enable: false,
    full_image_quality: 50,

    face_biodetection_enable: false,
    face_biodetection_threshold: 50,
    face_attr_enable: false,
    face_detect_targets: ["face"],

    face_ext_enable: true,
    face_capture_mode: "all",
    face_face_quality: 50,
    face_pedestrian_quality: 50,
    face_capture_interval: 0,
    face_push_mode: "fast",
    face_push_interval: 1000,
    face_max_push_number: 1,
    face_stranger_retry_count: 2,
    face_filter_roll: 45,
    face_filter_yaw: 35,
    face_filter_pitch: 45,
    face_filter_blurness: 60,
    face_filter_face_min: 80,
    face_filter_body_min: 80,
    face_global_stranger_enable: false,
    face_global_stranger_threshold: 20,

    structure_detect_targets: ["pedestrian"],
    structure_target_attr_enable: false,

    structure_ext_enable: true,
    structure_capture_mode: "all",
    structure_face_quality: 50,
    structure_pedestrian_quality: 50,
    structure_vehicle_quality: 50,
    structure_non_motor_quality: 50,
    structure_push_mode: "fast",
    structure_push_interval: 1000,
    structure_max_push_number: 1,
  }
}

function parseMonitor(m) {
  const cp = m.common_param || {}
  const ep = m.extend_param?.meg_box_param || {}
  const fp = cp.face_param || {}
  const sp = cp.structure_param || {}
  const efp = ep.face_param || {}
  const esp = ep.structure_param || {}

  return {
    monitor_name: cp.monitor_name || "",
    device_id: cp.device_id ?? 0,
    channel_type: cp.channel_type ?? 0,
    channel_id: cp.channel_id ?? 0,
    enable: cp.enable ?? true,
    alg_type: Array.isArray(cp.alg_type) && cp.alg_type.length > 0 ? cp.alg_type[0] : "",
    intelli_frame_enable: cp.intelli_frame_enable ?? false,
    full_image_enable: cp.full_image_param?.enable ?? false,
    full_image_quality: cp.full_image_param?.quality ?? 50,

    face_biodetection_enable: fp.biodetection_enable ?? false,
    face_biodetection_threshold: fp.biodetection_threshold ?? 50,
    face_attr_enable: fp.face_attr_enable ?? false,
    face_detect_targets: Array.isArray(fp.detect_target_list)
      ? [...fp.detect_target_list]
      : ["face"],

    face_ext_enable: efp.enable ?? true,
    face_capture_mode: efp.capture_param?.capture_mode || "all",
    face_face_quality: efp.capture_param?.face_quality ?? 50,
    face_pedestrian_quality: efp.capture_param?.pedestrian_quality ?? 50,
    face_capture_interval: efp.capture_param?.capture_interval ?? 0,
    face_push_mode: efp.push_param?.push_mode || "fast",
    face_push_interval: efp.push_param?.push_interval ?? 1000,
    face_max_push_number: efp.push_param?.max_push_number ?? 1,
    face_stranger_retry_count: efp.stranger_retry_count ?? 2,
    face_filter_roll: efp.filter?.roll ?? 45,
    face_filter_yaw: efp.filter?.yaw ?? 35,
    face_filter_pitch: efp.filter?.pitch ?? 45,
    face_filter_blurness: efp.filter?.blurness ?? 60,
    face_filter_face_min: efp.filter?.face_min ?? 80,
    face_filter_body_min: efp.filter?.body_min ?? 80,
    face_global_stranger_enable: efp.global_stranger_param?.enable ?? false,
    face_global_stranger_threshold: efp.global_stranger_param?.threshold ?? 20,

    structure_detect_targets: Array.isArray(sp.detect_target_list)
      ? [...sp.detect_target_list]
      : ["pedestrian"],
    structure_target_attr_enable: sp.target_attr_enable ?? false,

    structure_ext_enable: esp.enable ?? true,
    structure_capture_mode: esp.capture_param?.capture_mode || "all",
    structure_face_quality: esp.capture_param?.face_quality ?? 50,
    structure_pedestrian_quality: esp.capture_param?.pedestrian_quality ?? 50,
    structure_vehicle_quality: esp.capture_param?.vehicle_quality ?? 50,
    structure_non_motor_quality: esp.capture_param?.["non-motor_quality"] ?? 50,
    structure_push_mode: esp.push_param?.push_mode || "fast",
    structure_push_interval: esp.push_param?.push_interval ?? 1000,
    structure_max_push_number: esp.push_param?.max_push_number ?? 1,
  }
}

/**
 * Computes the next available monitor_id following the AIOTAP BOX convention:
 * monitor_id = device_id * 100 + index, where index ∈ [0, 16].
 */
function computeMonitorId(deviceId, allMonitors) {
  const base = deviceId * 100
  const usedIds = new Set(
    allMonitors.map((m) => m.common_param?.monitor_id).filter((id) => id != null),
  )
  for (let i = 1; i <= 16; i++) {
    if (!usedIds.has(base + i)) return base + i
  }
  return base + 1
}

function assemblePayload(form, monitorId, taskId, canvasRef) {
  const hasFace = form.alg_type === "face"
  const hasStructure = form.alg_type === "structure"

  const zones = canvasRef.current?.getZonesNormalized()
  const areasParam = canvasZonesToAreas(zones)

  const common_param = {
    monitor_id: monitorId,
    monitor_name: form.monitor_name,
    device_id: form.device_id,
    channel_type: form.channel_type,
    channel_id: form.channel_id,
    stream_id: form.device_id,
    enable: form.enable,
    alg_type: form.alg_type ? [form.alg_type] : [],
    intelli_frame_enable: form.intelli_frame_enable,
    negative_sample_filter_enable: true,
    object_frame_cfg: { enable: true },
  }

  if (hasFace) {
    common_param.face_param = {
      biodetection_enable: form.face_biodetection_enable,
      biodetection_threshold: form.face_biodetection_threshold,
      face_attr_enable: form.face_attr_enable,
      detect_target_list: form.face_detect_targets,
      group_list_info: [],
    }
    if (areasParam) common_param.face_param.areas_param = areasParam
  }

  if (hasStructure) {
    common_param.structure_param = {
      detect_target_list: form.structure_detect_targets,
      target_attr_enable: form.structure_target_attr_enable,
    }
    if (areasParam) common_param.structure_param.areas_param = areasParam
  }

  const extend_param = { meg_box_param: {} }

  if (hasFace) {
    const faceCapture = {
      capture_mode: form.face_capture_mode,
      face_quality: form.face_face_quality,
      pedestrian_quality: form.face_pedestrian_quality,
    }
    if (form.face_capture_interval > 0) {
      faceCapture.capture_interval = form.face_capture_interval
    }

    const facePush = {
      push_mode: form.face_push_mode,
      push_interval: form.face_push_interval,
      max_push_number: form.face_max_push_number,
    }

    extend_param.meg_box_param.face_param = {
      enable: form.face_ext_enable,
      capture_param: faceCapture,
      push_param: facePush,
      recog_param: { retry_count: 5, retry_interval: 5000 },
      stranger_retry_count: form.face_stranger_retry_count,
      filter: {
        roll: form.face_filter_roll,
        yaw: form.face_filter_yaw,
        pitch: form.face_filter_pitch,
        blurness: form.face_filter_blurness,
        face_min: form.face_filter_face_min,
        body_min: form.face_filter_body_min,
      },
      global_stranger_param: {
        enable: !!form.face_global_stranger_enable,
        threshold: form.face_global_stranger_threshold ?? 0,
        upload_best_stranger: false,
        schedule_plan: { schedule_plan_id: "1" },
      },
    }
  }

  if (hasStructure) {
    extend_param.meg_box_param.structure_param = {
      enable: form.structure_ext_enable,
      capture_param: {
        capture_mode: form.structure_capture_mode,
        face_quality: form.structure_face_quality,
        pedestrian_quality: form.structure_pedestrian_quality,
        vehicle_quality: form.structure_vehicle_quality,
        "non-motor_quality": form.structure_non_motor_quality,
      },
      push_param: {
        push_mode: form.structure_push_mode,
        push_interval: form.structure_push_interval,
        max_push_number: form.structure_max_push_number,
      },
    }
  }

  return { common_param, extend_param }
}

function validate(form) {
  const errors = []
  if (!form.monitor_name || form.monitor_name.length > 64) {
    errors.push("Le nom du moniteur est requis (1-64 caractères).")
  }
  if (!form.alg_type) {
    errors.push("Sélectionnez un type d'algorithme.")
  }
  if (form.alg_type === "face" && form.face_detect_targets.length === 0) {
    errors.push("Visage : sélectionnez au moins une cible de détection.")
  }
  if (form.alg_type === "structure" && form.structure_detect_targets.length === 0) {
    errors.push("Structure : sélectionnez au moins une cible de détection.")
  }
  return errors
}

export default function MonitorForm({ totemId, taskId, monitor, allMonitors = [], onSave, onCancel }) {
  const isEdit = !!monitor

  const [form, setForm] = useState(() =>
    isEdit ? parseMonitor(monitor) : buildDefaultForm(),
  )
  const [snapshot, setSnapshot] = useState(null)
  const [snapshotLoading, setSnapshotLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [validationErrors, setValidationErrors] = useState([])
  const [showFaceAdvanced, setShowFaceAdvanced] = useState(false)
  const [showStructAdvanced, setShowStructAdvanced] = useState(false)

  const effectiveId = useMemo(() => {
    if (isEdit) return monitor.common_param?.monitor_id
    return computeMonitorId(form.device_id, allMonitors)
  }, [isEdit, monitor, form.device_id, allMonitors])

  const canvasRef = useRef(null)

  const initialZones = useMemo(() => {
    if (!monitor) return null
    const faceAreas = monitor.common_param?.face_param?.areas_param
    const structAreas = monitor.common_param?.structure_param?.areas_param
    return areasToCanvasZones(faceAreas) || areasToCanvasZones(structAreas) || null
  }, [monitor])

  const set = useCallback((key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }))
  }, [])

  const setInt = useCallback((key, value) => {
    setForm((prev) => ({ ...prev, [key]: parseInt(value, 10) || 0 }))
  }, [])

  const toggleArrayItem = useCallback((key, item) => {
    setForm((prev) => {
      const arr = prev[key]
      return {
        ...prev,
        [key]: arr.includes(item)
          ? arr.filter((i) => i !== item)
          : [...arr, item],
      }
    })
  }, [])

  const captureSnapshot = useCallback(() => {
    setSnapshotLoading(true)
    setError(null)
    API.POST(
      `/admin/totem/${totemId}/ai-targeting-module/snapshot`,
      { device_id: form.device_id, channel_id: form.channel_id },
      (response) => {
        const b64 =
          response.data?.base64 || response.data?.data?.base64 || null
        setSnapshot(b64)
        setSnapshotLoading(false)
      },
      (errMsg) => {
        setError(errMsg || "Impossible de capturer le snapshot.")
        setSnapshotLoading(false)
      },
    )
  }, [totemId, form.device_id, form.channel_id])

  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault()
      setError(null)
      setValidationErrors([])

      const errs = validate(form)
      if (errs.length > 0) {
        setValidationErrors(errs)
        return
      }

      const payload = assemblePayload(form, effectiveId, taskId, canvasRef)
      setSaving(true)

      const url = `/admin/totem/${totemId}/ai-targeting-module/monitors${isEdit ? "" : "/create"}`
      const method = isEdit ? "PUT" : "POST"

      API.CUSTOM(
        method,
        url,
        payload,
        () => {
          setSaving(false)
          onSave?.()
        },
        (err) => {
          setSaving(false)
          setError(
            err?.response?.data?.error ||
            err?.message ||
            "Erreur lors de l'enregistrement.",
          )
        },
      )
    },
    [form, effectiveId, taskId, totemId, isEdit, onSave],
  )

  const hasFace = form.alg_type === "face"
  const hasStructure = form.alg_type === "structure"
  const busy = saving || snapshotLoading

  return (
    <form onSubmit={handleSubmit}>
      {validationErrors.length > 0 && (
        <div className="alert alert-warning py-2">
          <i className="fas fa-exclamation-triangle mr-1"></i>
          <ul className="mb-0 pl-3">
            {validationErrors.map((msg, i) => (
              <li key={i}>{msg}</li>
            ))}
          </ul>
        </div>
      )}

      {error && (
        <div className="alert alert-danger py-2">
          <i className="fas fa-exclamation-triangle mr-1"></i> {error}
        </div>
      )}

      {/* ── General Parameters ─────────────────────────── */}
      <div className="card card-primary card-outline">
        <div className="card-header py-2">
          <h3 className="card-title">
            <i className="fas fa-cog mr-1"></i> Paramètres généraux
          </h3>
        </div>
        <div className="card-body">
          <div className="row">
            <div className="col-md-6">
              <div className="form-group">
                <label htmlFor="mf_monitor_name">
                  Nom du moniteur <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  id="mf_monitor_name"
                  className="form-control form-control-sm"
                  value={form.monitor_name}
                  onChange={(e) => set("monitor_name", e.target.value)}
                  maxLength={64}
                  required
                  placeholder="Ex: Moniteur principal"
                />
              </div>
            </div>
            <div className="col-md-3">
              <div className="form-group">
                <label htmlFor="mf_device_id">ID Appareil</label>
                <input
                  type="number"
                  id="mf_device_id"
                  className="form-control form-control-sm"
                  value={form.device_id}
                  onChange={(e) => setInt("device_id", e.target.value)}
                  min={0}
                />
              </div>
            </div>
            <div className="col-md-3">
              <div className="form-group">
                <label>Activé</label>
                <div className="custom-control custom-switch mt-1">
                  <input
                    type="checkbox"
                    className="custom-control-input"
                    id="mf_enable"
                    checked={form.enable}
                    onChange={(e) => set("enable", e.target.checked)}
                  />
                  <label className="custom-control-label" htmlFor="mf_enable">
                    {form.enable ? "Oui" : "Non"}
                  </label>
                </div>
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-md-4">
              <div className="form-group">
                <label htmlFor="mf_channel_type">Type de canal</label>
                <select
                  id="mf_channel_type"
                  className="form-control form-control-sm"
                  value={form.channel_type}
                  onChange={(e) => setInt("channel_type", e.target.value)}
                >
                  <option value={0}>0 - Capteur</option>
                  <option value={1}>1 - Autre</option>
                </select>
              </div>
            </div>
            <div className="col-md-4">
              <div className="form-group">
                <label htmlFor="mf_channel_id">ID Canal</label>
                <input
                  type="number"
                  id="mf_channel_id"
                  className="form-control form-control-sm"
                  value={form.channel_id}
                  onChange={(e) => setInt("channel_id", e.target.value)}
                  min={0}
                />
              </div>
            </div>
          </div>

          <div className="form-group">
            <label>
              Type d'algorithme <span className="text-danger">*</span>
            </label>
            <div>
              <div className="custom-control custom-radio custom-control-inline">
                <input
                  type="radio"
                  className="custom-control-input"
                  id="mf_alg_face"
                  name="alg_type"
                  checked={form.alg_type === "face"}
                  onChange={() => set("alg_type", "face")}
                />
                <label className="custom-control-label" htmlFor="mf_alg_face">
                  <i className="fas fa-user mr-1"></i> Visage (face)
                </label>
              </div>
              <div className="custom-control custom-radio custom-control-inline">
                <input
                  type="radio"
                  className="custom-control-input"
                  id="mf_alg_structure"
                  name="alg_type"
                  checked={form.alg_type === "structure"}
                  onChange={() => set("alg_type", "structure")}
                />
                <label
                  className="custom-control-label"
                  htmlFor="mf_alg_structure"
                >
                  <i className="fas fa-object-group mr-1"></i> Structure
                </label>
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-md-4">
              <div className="custom-control custom-switch mb-2">
                <input
                  type="checkbox"
                  className="custom-control-input"
                  id="mf_intelli_frame"
                  checked={form.intelli_frame_enable}
                  onChange={(e) =>
                    set("intelli_frame_enable", e.target.checked)
                  }
                />
                <label
                  className="custom-control-label"
                  htmlFor="mf_intelli_frame"
                >
                  Frame intelligent
                </label>
              </div>
            </div>
            <div className="col-md-4">
              <div className="custom-control custom-switch mb-2">
                <input
                  type="checkbox"
                  className="custom-control-input"
                  id="mf_full_image"
                  checked={form.full_image_enable}
                  onChange={(e) => set("full_image_enable", e.target.checked)}
                />
                <label
                  className="custom-control-label"
                  htmlFor="mf_full_image"
                >
                  Image complète
                </label>
              </div>
            </div>
            {form.full_image_enable && (
              <div className="col-md-4">
                <label
                  htmlFor="mf_full_image_quality"
                  className="mb-0"
                  style={{ fontSize: "0.85rem" }}
                >
                  Qualité : {form.full_image_quality}%
                </label>
                <input
                  type="range"
                  id="mf_full_image_quality"
                  className="custom-range"
                  value={form.full_image_quality}
                  onChange={(e) => setInt("full_image_quality", e.target.value)}
                  min={0}
                  max={100}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Face Algorithm ──────────────────────────────── */}
      {hasFace && (
        <div className="card card-info card-outline">
          <div className="card-header py-2">
            <h3 className="card-title">
              <i className="fas fa-user mr-1"></i> Algorithme Visage
            </h3>
          </div>
          <div className="card-body">
            <div className="row">
              <div className="col-md-6">
                <div className="custom-control custom-switch mb-2">
                  <input
                    type="checkbox"
                    className="custom-control-input"
                    id="mf_face_bio"
                    checked={form.face_biodetection_enable}
                    onChange={(e) =>
                      set("face_biodetection_enable", e.target.checked)
                    }
                  />
                  <label
                    className="custom-control-label"
                    htmlFor="mf_face_bio"
                  >
                    Détection de vivacité
                  </label>
                </div>
              </div>
              <div className="col-md-6">
                <div className="custom-control custom-switch mb-2">
                  <input
                    type="checkbox"
                    className="custom-control-input"
                    id="mf_face_attr"
                    checked={form.face_attr_enable}
                    onChange={(e) =>
                      set("face_attr_enable", e.target.checked)
                    }
                  />
                  <label
                    className="custom-control-label"
                    htmlFor="mf_face_attr"
                  >
                    Analyse des attributs
                  </label>
                </div>
              </div>
            </div>

            {form.face_biodetection_enable && (
              <div className="form-group">
                <label
                  htmlFor="mf_face_bio_threshold"
                  style={{ fontSize: "0.85rem" }}
                >
                  Seuil de vivacité : {form.face_biodetection_threshold}
                </label>
                <input
                  type="range"
                  id="mf_face_bio_threshold"
                  className="custom-range"
                  value={form.face_biodetection_threshold}
                  onChange={(e) =>
                    setInt("face_biodetection_threshold", e.target.value)
                  }
                  min={0}
                  max={100}
                />
              </div>
            )}

            <div className="form-group">
              <label>
                Cibles de détection <span className="text-danger">*</span>
              </label>
              <div>
                {FACE_TARGETS.map((t) => (
                  <div
                    key={t.value}
                    className="custom-control custom-checkbox custom-control-inline"
                  >
                    <input
                      type="checkbox"
                      className="custom-control-input"
                      id={`mf_face_tgt_${t.value}`}
                      checked={form.face_detect_targets.includes(t.value)}
                      onChange={() =>
                        toggleArrayItem("face_detect_targets", t.value)
                      }
                    />
                    <label
                      className="custom-control-label"
                      htmlFor={`mf_face_tgt_${t.value}`}
                    >
                      {t.label}
                    </label>
                  </div>
                ))}
              </div>
            </div>

            <hr className="my-2" />
            <button
              type="button"
              className="btn btn-sm btn-link text-muted px-0"
              onClick={() => setShowFaceAdvanced((p) => !p)}
            >
              <i
                className={`fas fa-chevron-${showFaceAdvanced ? "up" : "down"} mr-1`}
              ></i>
              Paramètres avancés (BOX)
            </button>

            {showFaceAdvanced && (
              <div className="mt-2">
                <div className="custom-control custom-switch mb-3">
                  <input
                    type="checkbox"
                    className="custom-control-input"
                    id="mf_face_ext_enable"
                    checked={form.face_ext_enable}
                    onChange={(e) =>
                      set("face_ext_enable", e.target.checked)
                    }
                  />
                  <label
                    className="custom-control-label"
                    htmlFor="mf_face_ext_enable"
                  >
                    Algorithme activé
                  </label>
                </div>

                {/* Capture */}
                <fieldset className="border rounded p-2 mb-3">
                  <legend className="w-auto px-2" style={{ fontSize: "0.85rem" }}>
                    Capture
                  </legend>
                  <div className="row">
                    <div className="col-md-4">
                      <div className="form-group mb-2">
                        <label
                          htmlFor="mf_face_cap_mode"
                          style={{ fontSize: "0.85rem" }}
                        >
                          Mode
                        </label>
                        <select
                          id="mf_face_cap_mode"
                          className="form-control form-control-sm"
                          value={form.face_capture_mode}
                          onChange={(e) =>
                            set("face_capture_mode", e.target.value)
                          }
                        >
                          {CAPTURE_MODES.map((m) => (
                            <option key={m.value} value={m.value}>
                              {m.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="form-group mb-2">
                        <label
                          htmlFor="mf_face_cap_int"
                          style={{ fontSize: "0.85rem" }}
                        >
                          Intervalle (ms)
                        </label>
                        <input
                          type="number"
                          id="mf_face_cap_int"
                          className="form-control form-control-sm"
                          value={form.face_capture_interval}
                          onChange={(e) =>
                            setInt("face_capture_interval", e.target.value)
                          }
                          min={0}
                        />
                      </div>
                    </div>
                  </div>
                  {form.face_capture_mode === "custom" && (
                    <div className="row">
                      <div className="col-md-6">
                        <label style={{ fontSize: "0.85rem" }}>
                          Qualité visage : {form.face_face_quality}
                        </label>
                        <input
                          type="range"
                          className="custom-range"
                          value={form.face_face_quality}
                          onChange={(e) =>
                            setInt("face_face_quality", e.target.value)
                          }
                          min={0}
                          max={100}
                        />
                      </div>
                      <div className="col-md-6">
                        <label style={{ fontSize: "0.85rem" }}>
                          Qualité piéton : {form.face_pedestrian_quality}
                        </label>
                        <input
                          type="range"
                          className="custom-range"
                          value={form.face_pedestrian_quality}
                          onChange={(e) =>
                            setInt("face_pedestrian_quality", e.target.value)
                          }
                          min={0}
                          max={100}
                        />
                      </div>
                    </div>
                  )}
                </fieldset>

                {/* Push */}
                <fieldset className="border rounded p-2 mb-3">
                  <legend className="w-auto px-2" style={{ fontSize: "0.85rem" }}>
                    Envoi (Push)
                  </legend>
                  <div className="row">
                    <div className="col-md-4">
                      <div className="form-group mb-2">
                        <label
                          htmlFor="mf_face_push_mode"
                          style={{ fontSize: "0.85rem" }}
                        >
                          Mode
                        </label>
                        <select
                          id="mf_face_push_mode"
                          className="form-control form-control-sm"
                          value={form.face_push_mode}
                          onChange={(e) =>
                            set("face_push_mode", e.target.value)
                          }
                        >
                          {FACE_PUSH_MODES.map((m) => (
                            <option key={m.value} value={m.value}>
                              {m.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    {["fast", "best"].includes(form.face_push_mode) && (
                      <>
                        <div className="col-md-4">
                          <div className="form-group mb-2">
                            <label
                              htmlFor="mf_face_push_interval"
                              style={{ fontSize: "0.85rem" }}
                            >
                              Intervalle min d'envoi
                            </label>
                            <div className="input-group input-group-sm">
                              <input
                                type="number"
                                id="mf_face_push_interval"
                                className="form-control form-control-sm"
                                value={form.face_push_interval}
                                onChange={(e) =>
                                  setInt("face_push_interval", e.target.value)
                                }
                                min={500}
                                max={10000}
                              />
                              <div className="input-group-append">
                                <span className="input-group-text">ms (500-10000)</span>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="col-md-4">
                          <div className="form-group mb-2">
                            <label
                              htmlFor="mf_face_push_max"
                              style={{ fontSize: "0.85rem" }}
                            >
                              Nombre max (1-600)
                            </label>
                            <input
                              type="number"
                              id="mf_face_push_max"
                              className="form-control form-control-sm"
                              value={form.face_max_push_number}
                              onChange={(e) =>
                                setInt("face_max_push_number", e.target.value)
                              }
                              min={1}
                              max={600}
                            />
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </fieldset>

                {/* Filter */}
                <fieldset className="border rounded p-2 mb-3">
                  <legend className="w-auto px-2" style={{ fontSize: "0.85rem" }}>
                    Filtre qualité
                  </legend>
                  <div className="row">
                    {FILTER_FIELDS.map((f) => (
                      <div key={f.key} className="col-md-4">
                        <div className="form-group mb-2">
                          <label style={{ fontSize: "0.8rem" }}>
                            {f.label} : {form[f.key]}
                            <span
                              className="text-muted ml-1"
                              style={{ fontSize: "0.75rem" }}
                            >
                              ({f.rec})
                            </span>
                          </label>
                          <input
                            type="range"
                            className="custom-range"
                            value={form[f.key]}
                            onChange={(e) => setInt(f.key, e.target.value)}
                            min={f.min}
                            max={f.max}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </fieldset>

                {/* Stranger retry + global stranger */}
                <div className="row">
                  <div className="col-md-4">
                    <div className="form-group">
                      <label
                        htmlFor="mf_face_stranger_retry"
                        style={{ fontSize: "0.85rem" }}
                      >
                        Tentatives identification inconnu
                      </label>
                      <input
                        type="number"
                        id="mf_face_stranger_retry"
                        className="form-control form-control-sm"
                        value={form.face_stranger_retry_count}
                        onChange={(e) =>
                          setInt("face_stranger_retry_count", e.target.value)
                        }
                        min={0}
                      />
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="custom-control custom-switch mt-4">
                      <input
                        type="checkbox"
                        className="custom-control-input"
                        id="mf_face_gs_enable"
                        checked={form.face_global_stranger_enable}
                        onChange={(e) =>
                          set("face_global_stranger_enable", e.target.checked)
                        }
                      />
                      <label
                        className="custom-control-label"
                        htmlFor="mf_face_gs_enable"
                      >
                        Inconnu global
                      </label>
                    </div>
                  </div>
                  {form.face_global_stranger_enable && (
                    <div className="col-md-4">
                      <label style={{ fontSize: "0.85rem" }}>
                        Seuil inconnu : {form.face_global_stranger_threshold}
                      </label>
                      <input
                        type="range"
                        className="custom-range"
                        value={form.face_global_stranger_threshold}
                        onChange={(e) =>
                          setInt(
                            "face_global_stranger_threshold",
                            e.target.value,
                          )
                        }
                        min={0}
                        max={100}
                      />
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Structure Algorithm ─────────────────────────── */}
      {hasStructure && (
        <div className="card card-success card-outline">
          <div className="card-header py-2">
            <h3 className="card-title">
              <i className="fas fa-object-group mr-1"></i> Algorithme Structure
            </h3>
          </div>
          <div className="card-body">
            <div className="form-group">
              <label>
                Cibles de détection <span className="text-danger">*</span>
              </label>
              <div>
                {STRUCTURE_TARGETS.map((t) => (
                  <div
                    key={t.value}
                    className="custom-control custom-checkbox custom-control-inline"
                  >
                    <input
                      type="checkbox"
                      className="custom-control-input"
                      id={`mf_struct_tgt_${t.value}`}
                      checked={form.structure_detect_targets.includes(t.value)}
                      onChange={() =>
                        toggleArrayItem("structure_detect_targets", t.value)
                      }
                    />
                    <label
                      className="custom-control-label"
                      htmlFor={`mf_struct_tgt_${t.value}`}
                    >
                      {t.label}
                    </label>
                  </div>
                ))}
              </div>
            </div>

            <div className="custom-control custom-switch mb-3">
              <input
                type="checkbox"
                className="custom-control-input"
                id="mf_struct_attr"
                checked={form.structure_target_attr_enable}
                onChange={(e) =>
                  set("structure_target_attr_enable", e.target.checked)
                }
              />
              <label className="custom-control-label" htmlFor="mf_struct_attr">
                Analyse des attributs cible
              </label>
            </div>

            <hr className="my-2" />
            <button
              type="button"
              className="btn btn-sm btn-link text-muted px-0"
              onClick={() => setShowStructAdvanced((p) => !p)}
            >
              <i
                className={`fas fa-chevron-${showStructAdvanced ? "up" : "down"} mr-1`}
              ></i>
              Paramètres avancés (BOX)
            </button>

            {showStructAdvanced && (
              <div className="mt-2">
                <div className="custom-control custom-switch mb-3">
                  <input
                    type="checkbox"
                    className="custom-control-input"
                    id="mf_struct_ext_enable"
                    checked={form.structure_ext_enable}
                    onChange={(e) =>
                      set("structure_ext_enable", e.target.checked)
                    }
                  />
                  <label
                    className="custom-control-label"
                    htmlFor="mf_struct_ext_enable"
                  >
                    Algorithme activé
                  </label>
                </div>

                {/* Capture */}
                <fieldset className="border rounded p-2 mb-3">
                  <legend className="w-auto px-2" style={{ fontSize: "0.85rem" }}>
                    Capture
                  </legend>
                  <div className="row">
                    <div className="col-md-4">
                      <div className="form-group mb-2">
                        <label
                          htmlFor="mf_struct_cap_mode"
                          style={{ fontSize: "0.85rem" }}
                        >
                          Mode
                        </label>
                        <select
                          id="mf_struct_cap_mode"
                          className="form-control form-control-sm"
                          value={form.structure_capture_mode}
                          onChange={(e) =>
                            set("structure_capture_mode", e.target.value)
                          }
                        >
                          {CAPTURE_MODES.map((m) => (
                            <option key={m.value} value={m.value}>
                              {m.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                  {form.structure_capture_mode === "custom" && (
                    <div className="row">
                      {STRUCT_QUALITY_FIELDS.map((q) => (
                        <div key={q.key} className="col-md-3">
                          <div className="form-group mb-1">
                            <label style={{ fontSize: "0.8rem" }}>
                              {q.label} : {form[q.key]}
                            </label>
                            <input
                              type="range"
                              className="custom-range"
                              value={form[q.key]}
                              onChange={(e) => setInt(q.key, e.target.value)}
                              min={0}
                              max={100}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </fieldset>

                {/* Push */}
                <fieldset className="border rounded p-2 mb-3">
                  <legend className="w-auto px-2" style={{ fontSize: "0.85rem" }}>
                    Envoi (Push)
                  </legend>
                  <div className="row">
                    <div className="col-md-4">
                      <div className="form-group mb-2">
                        <label
                          htmlFor="mf_struct_push_mode"
                          style={{ fontSize: "0.85rem" }}
                        >
                          Mode
                        </label>
                        <select
                          id="mf_struct_push_mode"
                          className="form-control form-control-sm"
                          value={form.structure_push_mode}
                          onChange={(e) =>
                            set("structure_push_mode", e.target.value)
                          }
                        >
                          {PUSH_MODES.map((m) => (
                            <option key={m.value} value={m.value}>
                              {m.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    {form.structure_push_mode === "interval" && (
                      <div className="col-md-4">
                        <div className="form-group mb-2">
                          <label
                            htmlFor="mf_struct_push_int"
                            style={{ fontSize: "0.85rem" }}
                          >
                            Intervalle (ms)
                          </label>
                          <input
                            type="number"
                            id="mf_struct_push_int"
                            className="form-control form-control-sm"
                            value={form.structure_push_interval}
                            onChange={(e) =>
                              setInt("structure_push_interval", e.target.value)
                            }
                            min={0}
                          />
                        </div>
                      </div>
                    )}
                    {["fast", "best", "interval"].includes(form.structure_push_mode) && (
                      <div className="col-md-4">
                        <div className="form-group mb-2">
                          <label
                            htmlFor="mf_struct_push_max"
                            style={{ fontSize: "0.85rem" }}
                          >
                            Nombre max (1-600)
                          </label>
                          <input
                            type="number"
                            id="mf_struct_push_max"
                            className="form-control form-control-sm"
                            value={form.structure_max_push_number}
                            onChange={(e) =>
                              setInt(
                                "structure_max_push_number",
                                e.target.value,
                              )
                            }
                            min={1}
                            max={600}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </fieldset>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Detection Zones ────────────────────────────── */}
      {(hasFace || hasStructure) && (
        <div className="card card-warning card-outline">
          <div className="card-header py-2">
            <h3 className="card-title">
              <i className="fas fa-vector-square mr-1"></i> Zones de détection
            </h3>
          </div>
          <div className="card-body">
            <div className="mb-3">
              <button
                type="button"
                className="btn btn-info btn-sm"
                onClick={captureSnapshot}
                disabled={busy}
              >
                {snapshotLoading ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm mr-1"
                      role="status"
                    />
                    Capture en cours...
                  </>
                ) : (
                  <>
                    <i className="fas fa-camera mr-1"></i> Capturer un snapshot
                  </>
                )}
              </button>
            </div>

            {snapshot ? (
              <>
                <div className="text-center mb-2">
                  <DetectionZoneCanvas
                    ref={canvasRef}
                    base64Image={snapshot}
                    initialZones={initialZones}
                  />
                </div>
                <div
                  className="alert alert-info py-2 mb-0"
                  style={{ fontSize: "0.85rem" }}
                >
                  <i className="fas fa-info-circle mr-1"></i>
                  Dessinez des rectangles pour définir les zones de détection
                  actives. Les zones seront appliquées aux algorithmes
                  sélectionnés.
                  <span className="ml-1 text-muted">
                    (Clic droit ou touche Suppr pour supprimer une zone)
                  </span>
                </div>
              </>
            ) : (
              <p className="text-muted mb-0">
                <i className="fas fa-image mr-1"></i>
                Capturez un snapshot pour définir les zones de détection.
              </p>
            )}
          </div>
        </div>
      )}

      {/* ── Actions ────────────────────────────────────── */}
      <div className="d-flex justify-content-end mt-3">
        <button
          type="button"
          className="btn btn-default mr-2"
          onClick={onCancel}
          disabled={busy}
        >
          Annuler
        </button>
        <button type="submit" className="btn btn-primary" disabled={busy}>
          {saving ? (
            <>
              <span
                className="spinner-border spinner-border-sm mr-1"
                role="status"
              />
              Enregistrement...
            </>
          ) : (
            <>
              <i className="fas fa-save mr-1"></i>
              {isEdit ? "Mettre à jour" : "Créer"} le moniteur
            </>
          )}
        </button>
      </div>
    </form>
  )
}
